import { Stack } from "@mui/material";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { CbhIcon, IconType, List, ListItem, Text } from "@src/appV2/redesign/system/ui-components";
import { type ReactNode } from "react";

import { SegmentedText } from "./SegmentedText";
import { getCancellationProtectionDescription } from "./utils/getCancellationProtectionDescription";

interface CancellationProtectionTermsListProps {
  cancellationProtectionWindowInHours: number;
  isSentHomeProtected: boolean;
}

interface CancellationProtectionTermsListItemProps {
  iconType: IconType;
  title: string;
  children: ReactNode;
}

function TermsListItem(props: CancellationProtectionTermsListItemProps) {
  const { iconType, title, children } = props;
  return (
    <Stack direction="row" spacing={5} alignItems="flex-start">
      <CbhIcon type={iconType} />
      <Stack spacing={2}>
        <Text bold variant="body1" sx={{ color: (theme) => theme.palette.text.primary }}>
          {title}
        </Text>
        {typeof children === "string" ? (
          <Text variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
            {children}
          </Text>
        ) : (
          children
        )}
      </Stack>
    </Stack>
  );
}

export function CancellationProtectionTermsList(props: CancellationProtectionTermsListProps) {
  const { cancellationProtectionWindowInHours, isSentHomeProtected } = props;

  return (
    <Stack spacing={9}>
      <TermsListItem iconType="clock" title="Your time and income matter">
        <SegmentedText
          segments={getCancellationProtectionDescription(
            cancellationProtectionWindowInHours,
            isSentHomeProtected
          )}
          sx={{ color: (theme) => theme.palette.text.secondary }}
        />
      </TermsListItem>

      <TermsListItem iconType="building" title="Only applies when workplace cancels">
        <Text variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
          This protection doesn&apos;t apply in cases including, but not limited to:
        </Text>
        <List
          dense
          disablePadding
          sx={{
            listStyleType: "disc",
            paddingLeft: 7,
            color: (theme) => theme.palette.text.secondary,
          }}
        >
          {[
            "you cancelled your shift",
            "you called in sick",
            "you were sent home for performance reasons",
            "you arrived late to your shift",
          ].map((item) => (
            <ListItem
              key={item}
              disableGutters
              disablePadding
              sx={{ display: "list-item", padding: 0, minHeight: 0 }}
            >
              <Text variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
                {item}
              </Text>
            </ListItem>
          ))}
        </List>
        <Text variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
          {/* TODO: Add link to full policy */}
          View the{" "}
          <TextButton
            size="medium"
            sx={(theme) => ({
              display: "inline",
              height: "auto",
              verticalAlign: "baseline",
              color: theme.palette.text.secondary,
            })}
          >
            full policy
          </TextButton>{" "}
          to learn more
        </Text>
      </TermsListItem>

      <TermsListItem iconType="coin" title="No extra steps needed">
        Your full earnings will be automatically deposited into your account. We&apos;ll notify you
        once it&apos;s done.
      </TermsListItem>

      <TermsListItem iconType="calendar-remove" title="Your rate includes protection">
        Your rate may appear lower as a portion goes toward protecting your earnings
      </TermsListItem>
    </Stack>
  );
}
